// gate-registry.ts
//
// G0-G3 gate state (spec §8, F07). Standalone persisted projection —
// `<control_root>/gates.json`, written via writeFileDurable (P3) — rather than
// extending mission-state-reducer.ts's delta vocabulary. Judgment call
// (documented in evidence): mission-lock.ts/mission-state-reducer.ts risk
// concurrent-agent collision, and gate state has a narrow, self-contained
// shape (4 gate records) that doesn't need the full journal/projection
// machinery to be useful for M1's local-approval-path tests.
//
// Types + file I/O + request() live here; approve/waive/invalidate state
// transitions live in gate-registry-transitions.ts (split to stay ≤200 lines,
// both modules re-exported together so callers only import ONE module for
// "gate registry" — see index re-export at the bottom).

import { createHash } from "node:crypto";
import { join } from "node:path";
import { pathExists, readJson } from "../filesystem-helpers.js";
import { writeFileDurable } from "./durable-file-writer.js";

export type GateId = "g0" | "g1" | "g2" | "g3";
export type GateStatus = "not_requested" | "pending" | "approved" | "waived" | "invalidated";

export interface GateRecord {
  status: GateStatus;
  request_id?: string;
  artifact_hash?: string;
  actor_id?: string;
  at?: string;
  authority_ref?: string;
  reason?: string;
  /**
   * E1 (plan 20260915-1650 phase-04): phạm vi quyền do NGƯỜI duyệt khai, hiện
   * chỉ G3 (deploy) đọc.
   *
   * `targets`: danh sách môi trường được phép deploy tới. Trước phase này
   * `recheckG3ImmediatelyBeforeFirstSideEffect` có so `currentTarget` với
   * `approvedTarget`, nhưng cả hai vế đều lấy từ CÙNG `config.release.
   * target_branch` (`supervisor-release-flow-resume.ts`) nên phép so không bao
   * giờ sai — quyền thực tế là "deploy tới bất cứ đâu config trỏ".
   *
   * `expires_at`: ISO timestamp. Quá hạn ⇒ chặn, KHÔNG có đường tự gia hạn.
   *
   * CẢ HAI optional: gate duyệt kiểu cũ (không khai) giữ NGUYÊN hành vi cũ.
   * Ép cứng sẽ chặn mọi mission đang chạy — siết quyền không được phép là một
   * outage. Bật là quyết định của người duyệt, từng gate một.
   */
  targets?: string[];
  expires_at?: string;
}

export interface GateReceipt {
  gate: GateId;
  status: GateStatus;
  request_id?: string;
  actor_id?: string;
  at?: string;
  reused: boolean;
}

/**
 * `revision` (B1/OCC): bumped on every write. Read-modify-write callers pass
 * the revision they READ back to `writeGateFile`; a mismatch means another
 * writer committed in between and the write is refused rather than silently
 * overwriting that writer's decision. A gates.json written before B1 has no
 * field — `readGateFile` reads it as 0 (backwards compatible).
 */
export type GateFile = Record<GateId, GateRecord> & { revision: number };

export class GateRejectedError extends Error {
  readonly currentRequestId?: string;
  constructor(message: string, currentRequestId?: string) {
    super(message);
    this.name = "GateRejectedError";
    this.currentRequestId = currentRequestId;
  }
}

/**
 * Thrown when a write carries a revision older than the one on disk — the
 * only correct response is to re-read, re-apply the intent on top of the
 * winner's state, and write again. Deliberately NOT recoverable by forcing:
 * there is no "force write" flag, because the losing side always holds a
 * decision (an approve, an invalidate) that must not vanish unexamined.
 */
export class GateRevisionConflictError extends Error {
  readonly expectedRevision: number;
  readonly actualRevision: number;
  constructor(expectedRevision: number, actualRevision: number) {
    super(
      `gates.json đã đổi: ghi với revision ${expectedRevision} nhưng trên đĩa là ${actualRevision} — đọc lại rồi hoà giải, KHÔNG đè.`,
    );
    this.name = "GateRevisionConflictError";
    this.expectedRevision = expectedRevision;
    this.actualRevision = actualRevision;
  }
}

export const GATE_FILE_NAME = "gates.json";

function emptyGates(): GateFile {
  const empty: GateRecord = { status: "not_requested" };
  return { revision: 0, g0: { ...empty }, g1: { ...empty }, g2: { ...empty }, g3: { ...empty } };
}

export async function readGateFile(controlRoot: string): Promise<GateFile> {
  const path = join(controlRoot, GATE_FILE_NAME);
  if (!(await pathExists(path))) return emptyGates();
  try {
    const gates = await readJson<GateFile>(path);
    // A pre-B1 file carries no `revision` — treat it as 0 so the first write
    // on top of it lands at 1 (legacy files stay readable, spec §compat).
    return { ...gates, revision: typeof gates.revision === "number" ? gates.revision : 0 };
  } catch {
    return emptyGates();
  }
}

/**
 * Write gates.json, bumping `revision`. `expectedRevision` is the revision the
 * caller READ; if the file on disk has moved past it, another writer
 * (supervisor ↔ directive approve — the one genuinely concurrent pair, see
 * mission-lock.ts) committed in between and this write is refused.
 *
 * The re-read here closes the window as far as a single durable-rename
 * writer can: it does not make the check-then-write atomic against a truly
 * simultaneous process, but it does catch every interleaving where the other
 * writer's rename has landed — which is the observable failure this guards.
 */
export async function writeGateFile(
  controlRoot: string,
  gates: GateFile,
  expectedRevision: number,
): Promise<void> {
  const current = await readGateFile(controlRoot);
  if (current.revision !== expectedRevision) {
    throw new GateRevisionConflictError(expectedRevision, current.revision);
  }
  const path = join(controlRoot, GATE_FILE_NAME);
  const next: GateFile = { ...gates, revision: expectedRevision + 1 };
  await writeFileDurable(path, Buffer.from(`${JSON.stringify(next, null, 2)}\n`, "utf8"));
}

/**
 * Create a pending gate request (called by mission-initializer for G0/G1).
 * `reason` (B3) is recorded only when given — a re-request after
 * `invalidateGateOnShaChange` uses it to tell the approver WHY a fresh
 * request exists (old hash → new hash); a first request carries none.
 */
export async function requestGate(
  controlRoot: string,
  gate: GateId,
  requestId: string,
  artifactHash: string | undefined,
  now: () => Date = () => new Date(),
  reason?: string,
): Promise<GateFile> {
  const gates = await readGateFile(controlRoot);
  gates[gate] = {
    status: "pending",
    request_id: requestId,
    artifact_hash: artifactHash,
    at: now().toISOString(),
    ...(reason !== undefined ? { reason } : {}),
  };
  await writeGateFile(controlRoot, gates, gates.revision);
  return gates;
}

/** Deterministic idempotency-friendly request id from gate + revision context. */
export function deriveGateRequestId(gate: GateId, seed: string): string {
  const hash = createHash("sha256").update(seed, "utf8").digest("hex").slice(0, 8);
  return `${gate.toUpperCase()}-${hash}`;
}

// Re-export the transition operations so callers only need ONE import site
// for "gate registry" (test files, mission-initializer.ts, directive-inbox.ts).
export {
  approveGate,
  type GateApproveInput,
  type GateWaiveInput,
  invalidateGateOnShaChange,
  waiveGate,
} from "./gate-registry-transitions.js";

// Re-exported for callers that only import "gate-registry" (see header).
export type { GateFile as GateFileWithRevision };
